import { router } from "@inertiajs/react"
import {
  Check,
  Loader2,
  Maximize2,
  Paperclip,
  Pencil,
  Trash2,
  X,
} from "lucide-react"
import { useState } from "react"

import DocumentPreview from "@/components/document-preview"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { AttachPicker } from "./attach-picker"
import type { AttachTargets, InboxDocument } from "./inbox-types"
import { formatDate } from "./status-chip"

/**
 * One item in the pending-review queue, side by side with the document it came
 * from: preview left, what the model read and what it wants to do right.
 *
 * THE OPERATOR IS THE COMMIT. Confirm applies the proposal in one server-side
 * transaction; edit-then-confirm sends corrected values through the same path,
 * so a fixed date is applied exactly as a right-first-time one would be. There
 * is no way to accept a proposal without a person looking at it.
 */
export function InboxReview({
  document,
  attachTargets,
  confirmPath,
  rejectPath,
  attachPath,
  canEdit,
}: {
  document: InboxDocument
  attachTargets: AttachTargets
  confirmPath: string
  rejectPath: string
  attachPath: string
  canEdit: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [busy, setBusy] = useState<"confirm" | "reject" | "attach" | null>(null)
  const [error, setError] = useState("")

  const [updates, setUpdates] = useState<Record<string, string>>(() =>
    stringify(document.proposal.updates),
  )
  const [podDate, setPodDate] = useState(
    document.proposal.pod?.promised_on ?? "",
  )
  const [extracted, setExtracted] = useState<Record<string, string>>(() =>
    scalarFields(document),
  )
  const [attachWorkOrder, setAttachWorkOrder] = useState<number | null>(null)
  const [attachCar, setAttachCar] = useState<number | null>(null)

  // NOTE: selecting a different item must not carry the previous item's edits
  // across — they are different documents about different cars. That reset is
  // the page's `key={selected.id}`, which remounts this component; a
  // synchronizing effect here would be a second, redundant mechanism.

  const targeted = document.proposal.work_order_id != null
  // The proposal places the document in one of three ways: onto an existing
  // work order, onto a NEW one it wants to open, or nowhere (manual filing).
  // Only the third is "No work order matched".
  const creating = !targeted && document.proposal.create_type != null
  const placed = targeted || creating
  const updateKeys = Object.keys(updates)

  function confirmItem() {
    if (busy) return
    setBusy("confirm")
    setError("")
    router.post(
      confirmPath,
      {
        updates: nulled(updates),
        pod_promised_on: podDate || null,
        extracted: editing ? extracted : undefined,
      },
      {
        preserveScroll: true,
        onError: (errors) => {
          const first = Object.values(errors)[0]
          setError(typeof first === "string" ? first : "Could not confirm")
        },
        onFinish: () => setBusy(null),
      },
    )
  }

  function rejectItem() {
    if (busy) return
    if (!confirm("Discard this document? This cannot be undone.")) return
    setBusy("reject")
    router.post(rejectPath, {}, { onFinish: () => setBusy(null) })
  }

  function attachItem() {
    if (busy || (!attachWorkOrder && !attachCar)) return
    setBusy("attach")
    router.post(
      attachPath,
      { work_order_id: attachWorkOrder, car_id: attachCar },
      { preserveScroll: true, onFinish: () => setBusy(null) },
    )
  }

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
      <Card className="xl:sticky xl:top-4 xl:self-start">
        <CardHeader className="flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle>
              {document.filename ?? document.doc_type_label}
            </CardTitle>
            <CardDescription>
              Compare the document with what was read from it.
            </CardDescription>
          </div>
          {document.preview_url && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setFullscreen(true)}
            >
              <Maximize2 className="mr-1 size-4" />
              Fullscreen
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {document.preview_url ? (
            <DocumentPreview
              url={document.preview_url}
              alt={document.filename ?? "Document"}
              contentType={document.content_type}
              className="h-[60vh] xl:h-[75vh]"
            />
          ) : (
            <p className="text-muted-foreground text-sm">
              No file attached — this row is metadata only.
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog open={fullscreen} onOpenChange={setFullscreen}>
        <DialogContent className="max-w-[95vw]">
          <DialogHeader>
            <DialogTitle>
              {document.filename ?? document.doc_type_label}
            </DialogTitle>
          </DialogHeader>
          {document.preview_url && (
            <DocumentPreview
              url={document.preview_url}
              alt={document.filename ?? "Document"}
              contentType={document.content_type}
              className="h-[85vh]"
            />
          )}
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>Review</CardTitle>
            <Badge variant="outline">{document.doc_type_label}</Badge>
            {document.verification.status === "pass" && (
              <Badge className="border-emerald-400 bg-emerald-100 text-emerald-900 dark:border-emerald-700/60 dark:bg-emerald-950/50 dark:text-emerald-200">
                Verified
              </Badge>
            )}
            {document.verification.status === "fail" && (
              <Badge variant="destructive">Failed check</Badge>
            )}
          </div>
          <CardDescription>
            {document.sender_email
              ? `From ${document.sender_email} · ${formatDate(document.created_at)}`
              : formatDate(document.created_at)}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {document.verification.status === "fail" && (
            <Alert variant="destructive">
              <AlertTitle>This document did not pass verification</AlertTitle>
              <AlertDescription>
                <ul className="list-inside list-disc">
                  {document.verification.problems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Car</dt>
            <dd className="font-medium">
              {document.car_number ?? document.mark_number ?? "—"}
            </dd>
            {document.extracted.map((field) => (
              <ExtractedRow
                key={field.key}
                label={field.label}
                value={field.value}
                editing={editing && field.key in extracted}
                editValue={extracted[field.key] ?? ""}
                onChange={(next) =>
                  setExtracted((prev) => ({ ...prev, [field.key]: next }))
                }
              />
            ))}
          </dl>

          {document.confidence_notes && (
            <p className="text-muted-foreground text-xs italic">
              {document.confidence_notes}
            </p>
          )}

          {/* The proposal. Written as a sentence an operator can check against
              the document without decoding column names. */}
          <div className="bg-muted/40 rounded-lg border p-3">
            <p className="mb-2 text-sm font-medium">
              {targeted
                ? `Will update ${document.proposal.work_order_label ?? `WO #${document.proposal.work_order_id}`}`
                : creating
                  ? (document.proposal.create_label ??
                    `Will open a new ${document.proposal.create_type}`)
                  : "No work order matched"}
            </p>

            {targeted && updateKeys.length === 0 && !podDate && (
              <p className="text-muted-foreground text-sm">
                Nothing to change — confirming files this document on the work
                order.
              </p>
            )}

            <ul className="space-y-1.5 text-sm">
              {updateKeys.map((key) => (
                <li key={key} className="flex items-center gap-2">
                  <span className="text-muted-foreground">
                    {key.replace(/_/g, " ")} →
                  </span>
                  {editing ? (
                    <Input
                      value={updates[key]}
                      onChange={(event) =>
                        setUpdates((prev) => ({
                          ...prev,
                          [key]: event.target.value,
                        }))
                      }
                      className="h-8 w-44"
                      aria-label={key}
                    />
                  ) : (
                    <strong>{updates[key] || "—"}</strong>
                  )}
                </li>
              ))}
              {(podDate || editing) && targeted && (
                <li className="flex items-center gap-2">
                  <span className="text-muted-foreground">POD →</span>
                  {editing ? (
                    <Input
                      type="date"
                      value={podDate}
                      onChange={(event) => setPodDate(event.target.value)}
                      className="h-8 w-44"
                      aria-label="Promised-out date"
                    />
                  ) : (
                    <strong>{formatDate(podDate)}</strong>
                  )}
                </li>
              )}
            </ul>

            {document.proposal.note && (
              <p className="text-muted-foreground mt-2 text-xs">
                {document.proposal.note}
              </p>
            )}
          </div>

          {/* No work order matched AND none proposed: the operator files it by
              hand rather than the pipeline guessing onto the wrong car. A
              create proposal is already placed — Confirm opens the work order. */}
          {!placed && canEdit && (
            <div className="space-y-2 rounded-lg border border-dashed p-3">
              <Label className="text-xs">File this on</Label>
              <AttachPicker
                options={attachTargets.work_orders}
                value={attachWorkOrder}
                onChange={(value) => {
                  setAttachWorkOrder(value)
                  setAttachCar(null)
                }}
                placeholder="Search open work orders…"
                emptyText="No open work order matches."
              />
              <AttachPicker
                options={attachTargets.cars}
                value={attachCar}
                onChange={(value) => {
                  setAttachCar(value)
                  setAttachWorkOrder(null)
                }}
                placeholder="…or search cars"
                emptyText="No car matches."
              />
              <Button
                size="sm"
                variant="secondary"
                className="w-full"
                onClick={attachItem}
                disabled={busy !== null || (!attachWorkOrder && !attachCar)}
              >
                {busy === "attach" ? (
                  <Loader2 className="mr-1 size-4 animate-spin" />
                ) : (
                  <Paperclip className="mr-1 size-4" />
                )}
                Attach
              </Button>
            </div>
          )}

          {error && <p className="text-destructive text-sm">{error}</p>}

          {canEdit && (
            <div className="flex flex-wrap gap-2 border-t pt-4">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing((prev) => !prev)}
              >
                {editing ? (
                  <X className="mr-1 size-4" />
                ) : (
                  <Pencil className="mr-1 size-4" />
                )}
                {editing ? "Stop editing" : "Edit fields"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-green-500 text-green-700 hover:bg-green-50"
                onClick={confirmItem}
                disabled={busy !== null}
              >
                {busy === "confirm" ? (
                  <Loader2 className="mr-1 size-4 animate-spin" />
                ) : (
                  <Check className="mr-1 size-4" />
                )}
                {creating
                  ? `Create ${document.proposal.create_type_label ?? "work order"}`
                  : editing
                    ? "Save & confirm"
                    : "Confirm"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-red-500 text-red-700 hover:bg-red-50"
                onClick={rejectItem}
                disabled={busy !== null}
              >
                {busy === "reject" ? (
                  <Loader2 className="mr-1 size-4 animate-spin" />
                ) : (
                  <Trash2 className="mr-1 size-4" />
                )}
                Discard
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function ExtractedRow({
  label,
  value,
  editing,
  editValue,
  onChange,
}: {
  label: string
  value: unknown
  editing: boolean
  editValue: string
  onChange: (value: string) => void
}) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">
        {editing ? (
          <Input
            value={editValue}
            onChange={(event) => onChange(event.target.value)}
            className="h-8"
            aria-label={label}
          />
        ) : (
          renderValue(value)
        )}
      </dd>
    </>
  )
}

/**
 * Extracted values are whatever the contract says for that doc_type — a string,
 * a boolean, a list of release-form line items, a list of estimate line items
 * with amounts. Render each in the shape it arrived in rather than stringifying
 * the lot.
 */
function renderValue(value: unknown) {
  if (value == null || value === "") {
    return <span className="text-muted-foreground">—</span>
  }
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (Array.isArray(value)) {
    return (
      <ul className="list-inside list-disc">
        {value.map((item, index) => (
          <li key={index}>
            {typeof item === "object" && item !== null
              ? lineItemLabel(item as Record<string, unknown>)
              : String(item)}
          </li>
        ))}
      </ul>
    )
  }
  if (typeof value === "object") return JSON.stringify(value)
  return String(value)
}

function lineItemLabel(item: Record<string, unknown>): string {
  const description = item.description ?? item.name ?? ""
  const cents = item.amount_cents
  if (typeof cents === "number") {
    return `${description} — $${(cents / 100).toFixed(2)}`
  }
  return String(description)
}

function stringify(
  updates: Record<string, string | number | null>,
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(updates ?? {})) {
    out[key] = value == null ? "" : String(value)
  }
  return out
}

/** Blank means "clear it", which the server distinguishes from "leave it". */
function nulled(updates: Record<string, string>) {
  const out: Record<string, string | null> = {}
  for (const [key, value] of Object.entries(updates)) {
    out[key] = value === "" ? null : value
  }
  return out
}

/** Only scalar extracted fields are editable; a line-item list is not a form. */
function scalarFields(document: InboxDocument): Record<string, string> {
  const out: Record<string, string> = {}
  for (const field of document.extracted) {
    if (
      field.value == null ||
      typeof field.value === "object" ||
      Array.isArray(field.value)
    ) {
      continue
    }
    out[field.key] = String(field.value)
  }
  return out
}
